import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useState,
} from 'react'
import cx from 'classnames'
import { useCombobox } from 'downshift'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  size,
  useFloating,
} from '@floating-ui/react'
import type { Observable } from 'rxjs'
import { Help } from '@/components/Form'
import { type IconName } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Tooltip } from '@/components/Tooltip'
import { TypeBadge } from '@/features/legends/components/TypeBadge'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import { useObservable } from '@/hooks/rx-hooks'
import styles from './SymbolConnector.module.css'

/**
 * An entity a {@link SymbolConnector} can pick.
 */
export interface SymbolOption {
  id: string
  /**
   * What the entity is, like "Question" or "Variable".
   */
  kind: string
  valueType: RynoType | null
}

export interface SymbolConnectorProps {
  /**
   * The entities that can be picked, in the order they are listed.
   */
  options: readonly SymbolOption[]
  /**
   * The ID of the picked entity.
   */
  value: string | null
  /**
   * The input's text: the picked ID, or what is being searched.
   */
  searchText: string
  onSearchTextChange: (text: string) => unknown
  onChange: (id: string | null) => unknown
  /**
   * Called before the menu opens with every option, to clear the search.
   */
  onShowAll: () => unknown
  /**
   * Focuses the input and opens the menu when it emits.
   */
  focus$?: Observable<unknown>
  /**
   * Extra buttons in the input, after the "show all" button. See
   * {@link SymbolConnectorAction}.
   */
  actions?: ReactNode
  error?: string
  placeholder?: string
  isDisabled?: boolean
  'aria-label'?: string
  testId?: string
}

/**
 * Picks a symbol declared earlier in the legend. The input searches by ID,
 * and Tab picks the highlighted symbol.
 *
 * Not `CustomSelect`: this keeps the picked ID as the input's editable
 * text, picks with Tab, and puts action buttons inside the input, where
 * `CustomSelect` only has a toggle icon.
 */
export function SymbolConnector({
  options,
  value,
  searchText,
  onSearchTextChange,
  onChange,
  onShowAll,
  focus$,
  actions,
  error,
  placeholder,
  isDisabled,
  'aria-label': ariaLabel,
  testId,
}: SymbolConnectorProps) {
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null)
  const search = searchText.toLowerCase()
  const visibleOptions = search
    ? options.filter((o) => o.id.toLowerCase().includes(search))
    : options
  const items = visibleOptions.map((o) => o.id)
  const selected = options.find((o) => o.id === value) ?? null

  const {
    isOpen,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    openMenu,
    closeMenu,
    selectItem,
    setHighlightedIndex,
  } = useCombobox<string>({
    items,
    defaultHighlightedIndex: 0,
    inputValue: searchText,
    selectedItem: value,
    itemToString: (item) => item ?? '',
    onInputValueChange: ({ inputValue }) => onSearchTextChange(inputValue),
    onSelectedItemChange: ({ selectedItem }) => onChange(selectedItem ?? null),
    onStateChange: ({ type }) => {
      // Puts the picked ID back in the input once the menu closes.
      if (
        (type === useCombobox.stateChangeTypes.InputBlur ||
          type === useCombobox.stateChangeTypes.FunctionCloseMenu) &&
        searchText !== (value ?? '')
      ) {
        onSearchTextChange(value ?? '')
      }
    },
  })

  // A request can arrive in the commit that mounts the input, before
  // `inputEl` is rendered, so it waits for the element.
  const [focusRequestCount, setFocusRequestCount] = useState(0)
  useObservable(focus$, () => setFocusRequestCount((n) => n + 1))
  useEffect(() => {
    if (focusRequestCount > 0) {
      inputEl?.focus()
      openMenu()
    }
  }, [focusRequestCount, inputEl, openMenu])

  const {
    refs: { setReference, setFloating },
    floatingStyles,
  } = useFloating({
    open: isOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 12 }),
      size({
        padding: 12,
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${Math.max(rects.reference.width, 240)}px`,
            maxHeight: `${Math.min(300, availableHeight)}px`,
          })
        },
      }),
    ],
  })

  const showAll = () => {
    onShowAll()
    inputEl?.focus()
    openMenu()
    // The search is cleared, so the menu lists every option.
    const index = options.findIndex((o) => o.id === value)
    if (index > -1) {
      setHighlightedIndex(index)
    }
  }

  return (
    <div className={styles.root} data-test={testId}>
      <div
        ref={setReference}
        className={cx(
          styles.control,
          isOpen && styles.open,
          isDisabled && styles.disabled,
          error && styles.invalid,
        )}
      >
        <input
          {...getInputProps({
            ref: setInputEl,
            disabled: isDisabled,
            placeholder:
              placeholder ??
              (selected
                ? 'Search for symbols'
                : 'No symbol selected. Type here to search.'),
            autoComplete: 'off',
            autoCapitalize: 'off',
            autoCorrect: 'off',
            spellCheck: false,
            'aria-label': ariaLabel,
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              if (!e.target.value) {
                onChange(null)
              }
            },
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                // Closes the menu without clearing the input. SAFETY:
                // Downshift reads this flag off the native event, which its
                // typings leave out.
                ;(
                  e.nativeEvent as { preventDownshiftDefault?: boolean }
                ).preventDownshiftDefault = true
                closeMenu()
                return
              }
              if (
                e.key === 'Tab' &&
                !e.shiftKey &&
                isOpen &&
                highlightedIndex > -1
              ) {
                selectItem(items[highlightedIndex])
                closeMenu()
                e.preventDefault()
              }
            },
          })}
          className={styles.input}
          data-test={testId && `${testId}-input`}
        />
        {selected?.valueType && !isOpen && (
          <span className={styles.badge}>
            <TypeBadge type={selected.valueType} />
          </span>
        )}
        <div className={styles.actions}>
          <SymbolConnectorAction
            name={selected ? 'radio-checked' : 'radio-blank'}
            label="Show all possible symbols"
            isDisabled={isDisabled}
            onPress={showAll}
            testId={testId && `${testId}-show-all-button`}
          />
          {actions}
        </div>
      </div>
      <FloatingPortal>
        <ul
          {...getMenuProps(
            {
              ref: setFloating,
              style: floatingStyles,
              className: cx(styles.menu, !isOpen && styles.hidden),
            },
            { suppressRefError: true },
          )}
        >
          {isOpen &&
            (visibleOptions.length === 0 ? (
              <li className={styles.empty}>No symbols available here.</li>
            ) : (
              visibleOptions.map((option, index) => (
                <li
                  key={option.id}
                  className={cx(
                    styles.option,
                    highlightedIndex === index && styles.highlighted,
                    option.id === value && styles.selected,
                  )}
                  {...getItemProps({ item: option.id, index })}
                >
                  <span className={styles.optionId}>{option.id}</span>
                  <span className={styles.optionKind}>{option.kind}</span>
                  {option.valueType && <TypeBadge type={option.valueType} />}
                </li>
              ))
            ))}
        </ul>
      </FloatingPortal>
      <Help error={error} />
    </div>
  )
}

export interface SymbolConnectorActionProps {
  name: IconName
  label: string
  onPress: () => unknown
  isDisabled?: boolean
  testId?: string
}

/**
 * A button inside a {@link SymbolConnector}'s input, like adding another
 * connector to a list.
 */
export function SymbolConnectorAction({
  name,
  label,
  onPress,
  isDisabled,
  testId,
}: SymbolConnectorActionProps) {
  return (
    <Tooltip content={label}>
      <IconButton
        name={name}
        label={label}
        size={14}
        className={styles.action}
        isDisabled={isDisabled}
        onPress={onPress}
        testId={testId}
      />
    </Tooltip>
  )
}
